/**
 * Partner Wallet Tab — Wallet overview, transactions, billable missions, payout requests
 * For fleet managers in the partner dashboard
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Wallet, ArrowUpCircle, ArrowDownCircle, Euro, Clock,
  CheckCircle, XCircle, FileText, Upload, Loader2, AlertTriangle,
  List, Receipt, Banknote, Send
} from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const fmtCurrency = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

const statusBadge = (status) => {
  const map = {
    PENDING: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
    APPROVED: { label: 'Approuvé', cls: 'bg-emerald-500/20 text-emerald-400' },
    REJECTED: { label: 'Rejeté', cls: 'bg-red-500/20 text-red-400' },
    PAID: { label: 'Payé', cls: 'bg-blue-500/20 text-blue-400' },
  };
  const s = map[status] || { label: status, cls: 'bg-gray-500/20 text-gray-400' };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
};

const txTypeBadge = (type) => {
  const map = {
    MISSION_CREDIT: { label: 'Crédit mission', cls: 'text-emerald-400', icon: ArrowUpCircle },
    PAYOUT_REQUEST: { label: 'Demande versement', cls: 'text-amber-400', icon: ArrowDownCircle },
    PAYOUT_APPROVED: { label: 'Versement approuvé', cls: 'text-blue-400', icon: CheckCircle },
    PAYOUT_REJECTED: { label: 'Versement rejeté', cls: 'text-red-400', icon: XCircle },
    CASH_COLLECTION: { label: 'Espèces reçues', cls: 'text-orange-400', icon: Banknote },
    CASH_RETURNED: { label: 'Espèces remises', cls: 'text-emerald-400', icon: Send },
  };
  const t = map[type] || { label: type, cls: 'text-gray-400', icon: List };
  const Icon = t.icon;
  return <span className={`flex items-center gap-1 text-xs ${t.cls}`}><Icon className="w-3.5 h-3.5" />{t.label}</span>;
};

export default function PartnerWalletTab() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [missions, setMissions] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('overview');
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [walletRes, txRes, missionsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/wallet`, { headers: getHeaders() }),
        axios.get(`${API}/wallet/transactions?limit=100`, { headers: getHeaders() }),
        axios.get(`${API}/wallet/missions`, { headers: getHeaders() }),
        axios.get(`${API}/wallet/payout-requests`, { headers: getHeaders() }),
      ]);
      setWallet(walletRes.data.wallet);
      setTransactions(txRes.data.transactions || []);
      setMissions(missionsRes.data.missions || []);
      setPayoutRequests(payoutsRes.data.payout_requests || []);
    } catch (err) {
      console.error('Wallet fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePayoutSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) { setError('Montant invalide'); return; }
    if (!invoiceFile) { setError('Facture obligatoire'); return; }
    if (wallet && amount > wallet.balance) { setError(`Solde insuffisant (${fmtCurrency(wallet.balance)})`); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', amount);
      formData.append('invoice_file', invoiceFile);
      await axios.post(`${API}/wallet/payout-request`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setShowPayoutForm(false);
      setPayoutAmount('');
      setInvoiceFile(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  const subTabs = [
    { key: 'overview', label: 'Aperçu', icon: Wallet },
    { key: 'missions', label: 'Missions', icon: Receipt },
    { key: 'transactions', label: 'Transactions', icon: List },
    { key: 'payouts', label: 'Versements', icon: ArrowDownCircle },
  ];

  return (
    <div className="space-y-4" data-testid="wallet-tab">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-[#1A1A1A] rounded-lg p-1">
        {subTabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setSubTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 rounded-md text-[10px] sm:text-xs font-medium transition-all
                ${subTab === t.key ? 'bg-[#C9A24D] text-[#0B0B0B]' : 'text-[#E5E5E5]/50 hover:text-[#E5E5E5]/80'}`}
              data-testid={`wallet-subtab-${t.key}`}>
              <Icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Overview */}
      {subTab === 'overview' && wallet && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/30">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-[#E5E5E5]/40 text-[10px] sm:text-xs mb-1">Solde actuel</p>
                <p className="text-lg sm:text-2xl font-bold text-[#C9A24D]" data-testid="wallet-balance">{fmtCurrency(wallet.balance)}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1F1F1F] border-emerald-500/20">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-[#E5E5E5]/40 text-[10px] sm:text-xs mb-1">Total crédité</p>
                <p className="text-base sm:text-xl font-bold text-emerald-400" data-testid="wallet-total-credited">{fmtCurrency(wallet.total_credited)}</p>
              </CardContent>
            </Card>
            {(wallet.total_cash_collected || 0) > 0 && (
              <Card className="bg-[#1F1F1F] border-orange-500/20">
                <CardContent className="p-3 sm:p-4 text-center">
                  <p className="text-[#E5E5E5]/40 text-[10px] sm:text-xs mb-1">Espèces reçues</p>
                  <p className="text-base sm:text-xl font-bold text-orange-400" data-testid="wallet-total-cash">{fmtCurrency(wallet.total_cash_collected)}</p>
                </CardContent>
              </Card>
            )}
            <Card className="bg-[#1F1F1F] border-blue-500/20">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-[#E5E5E5]/40 text-[10px] sm:text-xs mb-1">Total versé</p>
                <p className="text-base sm:text-xl font-bold text-blue-400" data-testid="wallet-total-paid">{fmtCurrency(wallet.total_paid_out)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Billing breakdown when cash is involved */}
          {(wallet.total_cash_collected || 0) > 0 && (
            <Card className="bg-[#1F1F1F] border-orange-500/20">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold text-xs mb-2 flex items-center gap-2">
                  <Receipt className="w-3.5 h-3.5 text-[#C9A24D]" /> Détail facturation
                </h3>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-[#E5E5E5]/40">Total missions (facture)</span><span className="text-emerald-400 font-semibold">{fmtCurrency(wallet.total_credited)}</span></div>
                  <div className="flex justify-between"><span className="text-[#E5E5E5]/40">Espèces reçues (avoir)</span><span className="text-orange-400 font-semibold">-{fmtCurrency(wallet.total_cash_collected)}</span></div>
                  <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><span className="text-white font-medium">Virement attendu</span><span className="text-[#C9A24D] font-bold">{fmtCurrency(wallet.balance)}</span></div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payout request button */}
          {!showPayoutForm && wallet.balance > 0 && !wallet.has_pending_payout && (
            <Button onClick={() => setShowPayoutForm(true)}
              className="w-full bg-[#C9A24D] text-[#0B0B0B] hover:bg-[#B8913C] font-semibold"
              data-testid="request-payout-btn">
              <ArrowDownCircle className="w-4 h-4 mr-2" />Demander un versement
            </Button>
          )}
          {wallet.has_pending_payout && (
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
              <Clock className="w-4 h-4" /> Une demande de versement est déjà en cours de traitement
            </div>
          )}

          {/* Payout form */}
          {showPayoutForm && (
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/30">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <ArrowDownCircle className="w-4 h-4 text-[#C9A24D]" /> Nouvelle demande de versement
                </h3>
                <form onSubmit={handlePayoutSubmit} className="space-y-3">
                  <div>
                    <label className="block text-[#E5E5E5]/50 text-xs mb-1">Montant (EUR) *</label>
                    <input type="number" step="0.01" min="0.01" max={wallet.balance}
                      value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)}
                      className="w-full bg-[#2A2A2A] border border-[#C9A24D]/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A24D]"
                      placeholder={`Max: ${fmtCurrency(wallet.balance)}`}
                      data-testid="payout-amount-input" />
                  </div>
                  <div>
                    <label className="block text-[#E5E5E5]/50 text-xs mb-1">Facture justificative (PDF, JPG, PNG) *</label>
                    <label className="flex items-center gap-2 w-full bg-[#2A2A2A] border border-dashed border-[#C9A24D]/30 rounded-lg px-3 py-3 cursor-pointer hover:border-[#C9A24D]/50 transition-colors"
                      data-testid="payout-invoice-upload">
                      <Upload className="w-4 h-4 text-[#C9A24D]" />
                      <span className="text-[#E5E5E5]/50 text-xs">{invoiceFile ? invoiceFile.name : 'Choisir un fichier...'}</span>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                        onChange={e => setInvoiceFile(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                  {error && (
                    <p className="text-red-400 text-xs flex items-center gap-1" data-testid="payout-error">
                      <AlertTriangle className="w-3 h-3" />{error}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button type="submit" disabled={submitting}
                      className="flex-1 bg-[#C9A24D] text-[#0B0B0B] hover:bg-[#B8913C] font-semibold"
                      data-testid="payout-submit-btn">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Soumettre'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setShowPayoutForm(false); setError(''); }}
                      className="border-[#C9A24D]/30 text-[#E5E5E5]/60 hover:bg-[#2A2A2A]">
                      Annuler
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Recent transactions */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-2">Dernières transactions</h3>
            {transactions.length === 0 ? (
              <p className="text-[#E5E5E5]/30 text-xs">Aucune transaction</p>
            ) : (
              <div className="space-y-1.5">
                {transactions.slice(0, 5).map(tx => (
                  <div key={tx.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-lg px-3 py-2">
                    <div>
                      {txTypeBadge(tx.type)}
                      <p className="text-[#E5E5E5]/40 text-[10px] mt-0.5">{fmtDate(tx.created_at)}</p>
                    </div>
                    <span className={`text-sm font-semibold ${parseFloat(tx.amount) >= 0 ? 'text-emerald-400' : tx.type === 'CASH_COLLECTION' ? 'text-orange-400' : 'text-red-400'}`}>
                      {parseFloat(tx.amount) >= 0 ? '+' : ''}{fmtCurrency(parseFloat(tx.amount))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Missions Tab */}
      {subTab === 'missions' && (
        <div className="space-y-2" data-testid="wallet-missions-list">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Receipt className="w-4 h-4 text-[#C9A24D]" /> Missions créditées ({missions.length})
          </h3>
          {missions.length === 0 ? (
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardContent className="p-6 text-center">
                <Receipt className="w-8 h-8 text-[#C9A24D]/20 mx-auto mb-2" />
                <p className="text-[#E5E5E5]/30 text-xs">Aucune mission créditée</p>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#E5E5E5]/40 border-b border-[#333]">
                    <th className="text-left py-2 px-2">Référence</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Client</th>
                    <th className="text-right py-2 px-2">Montant TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {missions.map(m => (
                    <tr key={m.mission_id} className="border-b border-[#222] text-[#E5E5E5]/70 hover:bg-[#1A1A1A]">
                      <td className="py-2 px-2 font-mono text-[#C9A24D]">{m.reference || m.mission_id?.slice(0, 8)}</td>
                      <td className="py-2 px-2">{fmtDate(m.date)}</td>
                      <td className="py-2 px-2">{m.client_name || '—'}</td>
                      <td className="py-2 px-2 text-right font-semibold text-emerald-400">{fmtCurrency(m.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {subTab === 'transactions' && (
        <div className="space-y-2" data-testid="wallet-transactions-list">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <List className="w-4 h-4 text-[#C9A24D]" /> Historique des transactions ({transactions.length})
          </h3>
          {transactions.length === 0 ? (
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardContent className="p-6 text-center">
                <List className="w-8 h-8 text-[#C9A24D]/20 mx-auto mb-2" />
                <p className="text-[#E5E5E5]/30 text-xs">Aucune transaction</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-1.5">
              {transactions.map(tx => (
                <div key={tx.id} className="flex items-center justify-between bg-[#1A1A1A] rounded-lg px-3 py-2.5">
                  <div>
                    {txTypeBadge(tx.type)}
                    <p className="text-[#E5E5E5]/30 text-[10px] mt-0.5">{tx.reference}</p>
                    <p className="text-[#E5E5E5]/20 text-[10px]">{fmtDate(tx.created_at)}</p>
                  </div>
                  <span className={`text-sm font-semibold ${parseFloat(tx.amount) >= 0 ? 'text-emerald-400' : tx.type === 'CASH_COLLECTION' ? 'text-orange-400' : 'text-red-400'}`}>
                    {parseFloat(tx.amount) >= 0 ? '+' : ''}{fmtCurrency(parseFloat(tx.amount))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payouts Tab */}
      {subTab === 'payouts' && (
        <div className="space-y-2" data-testid="wallet-payouts-list">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <ArrowDownCircle className="w-4 h-4 text-[#C9A24D]" /> Demandes de versement ({payoutRequests.length})
          </h3>
          {payoutRequests.length === 0 ? (
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardContent className="p-6 text-center">
                <ArrowDownCircle className="w-8 h-8 text-[#C9A24D]/20 mx-auto mb-2" />
                <p className="text-[#E5E5E5]/30 text-xs">Aucune demande de versement</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {payoutRequests.map(p => (
                <Card key={p.id} className="bg-[#1F1F1F] border-[#C9A24D]/20">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        {statusBadge(p.status)}
                        <span className="text-white font-semibold text-sm">{fmtCurrency(p.requested_amount)}</span>
                      </div>
                      <p className="text-[#E5E5E5]/30 text-[10px]">{fmtDate(p.created_at)}</p>
                      {p.admin_comment && <p className="text-[#E5E5E5]/40 text-[10px] mt-0.5 italic">"{p.admin_comment}"</p>}
                    </div>
                    {p.invoice_signed_url && (
                      <a href={p.invoice_signed_url} target="_blank" rel="noopener noreferrer"
                        className="text-[#C9A24D] hover:text-[#B8913C] text-xs flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />Facture
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
